/**
 * Sheet — Datensatz-Karte von rechts (UX_KONZEPT.md §7 / §7.2), resizable.
 *
 * Portiert 1:1 aus rmnc-personal (components/ui/sheet.tsx) auf
 * @radix-ui/react-dialog — docs/SHELL_PORTIERUNG.md + KARTEN_FAEHIGKEIT_AUDIT.md
 * Prio 1 ("Resizable-sheet.tsx aus rmnc-personal in die drei anderen Apps
 * übernehmen"). Ersetzt die frühere selbst-enthaltene Variante (kein Radix);
 * die öffentliche API (Sheet/SheetContent/SheetHeader/SheetTitle/
 * SheetDescription/SheetFooter) war bewusst schon shadcn-kompatibel gehalten,
 * daher ändert sich an den Callsites nur: `sheetKey`/`defaultWidth` ergänzen,
 * das nun überflüssige `onOpenChange`-Prop an <SheetContent> entfernen (Radix
 * Context übernimmt das Schließen).
 */
import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { XIcon } from "lucide-react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const Sheet = DialogPrimitive.Root

const SheetTrigger = DialogPrimitive.Trigger

const SheetClose = DialogPrimitive.Close

const SheetPortal = DialogPrimitive.Portal

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    className={cn(
      "fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
    ref={ref}
  />
))
SheetOverlay.displayName = DialogPrimitive.Overlay.displayName

const sheetVariants = cva(
  "fixed z-50 gap-4 bg-white p-6 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-3/4 border-r border-slate-200 data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
        right:
          "inset-y-0 right-0 h-full w-3/4 border-l border-slate-200 data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm",
      },
    },
    defaultVariants: {
      side: "right",
    },
  }
)

// --- Resizable-Breite (UX_KONZEPT.md §7.2) --------------------------------
// Rechts-Sheets mit `sheetKey` sind in der Breite verstellbar: Ziehgriff an
// der linken Kante, Persistenz pro sheetKey in localStorage, Doppelklick auf
// den Griff setzt auf `defaultWidth` zurück, Pfeiltasten verstellen (a11y).
// Mobile (< sm): kein Resize, volle Breite (w-full).

/** Startbreiten in px — entsprechen den bisherigen Klassen
 * sm:max-w-md (448), sm:max-w-lg (512), sm:max-w-xl (576). */
export const SHEET_WIDTH = { md: 448, lg: 512, xl: 576 } as const

const SHEET_MIN_WIDTH = 360
const SHEET_MAX_VW = 0.9
const SHEET_WIDTH_STORAGE_PREFIX = "rmnc-technik.sheet-width:"
const SHEET_WIDTH_KEY_STEP = 24

function maxSheetWidth(): number {
  if (typeof window === "undefined") return 2000
  return Math.max(Math.round(window.innerWidth * SHEET_MAX_VW), SHEET_MIN_WIDTH)
}

function clampSheetWidth(width: number): number {
  return Math.min(Math.max(width, SHEET_MIN_WIDTH), maxSheetWidth())
}

function loadSheetWidth(sheetKey: string): number | null {
  try {
    const raw = localStorage.getItem(SHEET_WIDTH_STORAGE_PREFIX + sheetKey)
    if (!raw) return null
    const value = Number(raw)
    return Number.isFinite(value) ? value : null
  } catch {
    return null // localStorage nicht verfügbar (privater Modus o. ä.)
  }
}

function saveSheetWidth(sheetKey: string, width: number | null) {
  try {
    if (width == null) {
      localStorage.removeItem(SHEET_WIDTH_STORAGE_PREFIX + sheetKey)
    } else {
      localStorage.setItem(SHEET_WIDTH_STORAGE_PREFIX + sheetKey, String(Math.round(width)))
    }
  } catch {
    // localStorage nicht verfügbar — Breite gilt nur für die Sitzung
  }
}

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
  /** Stabiler Schlüssel für die Breiten-Persistenz (localStorage).
   * Nur mit `side="right"` (Default) wirksam; ohne sheetKey kein Resize. */
  sheetKey?: string
  /** Startbreite in px (Default 448 = bisheriges sm:max-w-md);
   * für lg/xl `SHEET_WIDTH.lg` / `SHEET_WIDTH.xl` verwenden. */
  defaultWidth?: number
  /** @deprecated seit Umstieg auf Radix übernimmt der Sheet-Context das
   * Schließen; Prop wird ignoriert und nur für abwärtskompatible Callsites
   * (vor dem Aufräumen) toleriert. */
  onOpenChange?: (open: boolean) => void
}

const SheetContent = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Content>, SheetContentProps>(
  (
    { side = "right", className, style, children, sheetKey, defaultWidth = SHEET_WIDTH.md, onOpenChange: _ignored, ...props },
    ref
  ) => {
    const resizable = sheetKey != null && side === "right"

    const [storedWidth, setStoredWidth] = React.useState<number | null>(() =>
      sheetKey != null ? loadSheetWidth(sheetKey) : null
    )
    const [dragging, setDragging] = React.useState(false)

    // Bei Wechsel des sheetKey (z. B. wiederverwendete Komponente) neu laden
    React.useEffect(() => {
      if (sheetKey != null) setStoredWidth(loadSheetWidth(sheetKey))
    }, [sheetKey])

    // Render-seitig nur nach unten clampen — das Maximum (90vw) erledigt die
    // CSS-Klasse sm:max-w-[90vw] live (robust gegen Fenster-Resize, ohne Listener)
    const width = Math.max(storedWidth ?? defaultWidth, SHEET_MIN_WIDTH)
    const widthRef = React.useRef(width)
    widthRef.current = width

    function setAndPersist(next: number) {
      if (sheetKey == null) return
      const clamped = clampSheetWidth(next)
      widthRef.current = clamped // synchron, damit schnelle Tastenfolgen kumulieren
      setStoredWidth(clamped)
      saveSheetWidth(sheetKey, clamped)
    }

    function resetWidth() {
      if (sheetKey == null) return
      setStoredWidth(null)
      saveSheetWidth(sheetKey, null)
    }

    function onHandlePointerDown(e: React.PointerEvent<HTMLDivElement>) {
      e.preventDefault()
      try {
        e.currentTarget.setPointerCapture(e.pointerId)
      } catch {
        // Pointer-Capture nicht verfügbar — Drag funktioniert trotzdem,
        // solange der Zeiger über dem Griff bleibt
      }
      setDragging(true)
    }

    function onHandlePointerMove(e: React.PointerEvent<HTMLDivElement>) {
      if (!dragging) return
      // Sheet klebt an der rechten Fensterkante → Breite = Fensterbreite − Zeiger-X
      setStoredWidth(clampSheetWidth(window.innerWidth - e.clientX))
    }

    function onHandlePointerUp(e: React.PointerEvent<HTMLDivElement>) {
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId)
      }
      if (!dragging) return
      setDragging(false)
      if (sheetKey != null) saveSheetWidth(sheetKey, widthRef.current)
    }

    function onHandleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
      switch (e.key) {
        case "ArrowLeft": // Sheet sitzt rechts → links ziehen = breiter
        case "ArrowUp":
          e.preventDefault()
          setAndPersist(widthRef.current + SHEET_WIDTH_KEY_STEP)
          break
        case "ArrowRight":
        case "ArrowDown":
          e.preventDefault()
          setAndPersist(widthRef.current - SHEET_WIDTH_KEY_STEP)
          break
        case "Home":
          e.preventDefault()
          setAndPersist(maxSheetWidth())
          break
        case "End":
          e.preventDefault()
          setAndPersist(SHEET_MIN_WIDTH)
          break
      }
    }

    return (
      <SheetPortal>
        <SheetOverlay />
        <DialogPrimitive.Content
          ref={ref}
          style={
            resizable
              ? ({
                  ...style,
                  "--sheet-width": `${width}px`,
                } as React.CSSProperties)
              : style
          }
          className={cn(
            sheetVariants({ side }),
            dragging && "transition-none",
            className,
            // nach className, damit die Resize-Breite alte sm:max-w-*-Klassen schlägt
            resizable && "w-full max-w-none sm:w-[var(--sheet-width)] sm:max-w-[90vw]"
          )}
          {...props}
        >
          {resizable && (
            <div
              role="separator"
              aria-orientation="vertical"
              aria-label="Breite anpassen (Pfeiltasten; Doppelklick setzt zurück)"
              aria-valuemin={SHEET_MIN_WIDTH}
              aria-valuemax={maxSheetWidth()}
              aria-valuenow={Math.round(width)}
              tabIndex={0}
              onPointerDown={onHandlePointerDown}
              onPointerMove={onHandlePointerMove}
              onPointerUp={onHandlePointerUp}
              onPointerCancel={onHandlePointerUp}
              onDoubleClick={resetWidth}
              onKeyDown={onHandleKeyDown}
              className="absolute inset-y-0 left-0 z-10 hidden w-2 cursor-col-resize touch-none items-center justify-center hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 sm:flex"
            >
              <div className="h-8 w-1 shrink-0 rounded-full bg-slate-300" />
            </div>
          )}
          <DialogPrimitive.Close
            aria-label="Schließen"
            className="absolute right-4 top-4 rounded-sm p-1 text-slate-500 opacity-70 transition-opacity hover:bg-slate-100 hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 disabled:pointer-events-none"
          >
            <XIcon className="h-4 w-4" />
            <span className="sr-only">Schließen</span>
          </DialogPrimitive.Close>
          {children}
        </DialogPrimitive.Content>
      </SheetPortal>
    )
  }
)
SheetContent.displayName = DialogPrimitive.Content.displayName

const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("mb-4 flex flex-col space-y-1.5 pr-8 text-left", className)} {...props} />
)
SheetHeader.displayName = "SheetHeader"

const SheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "mt-6 flex flex-col-reverse gap-2 border-t border-slate-100 pt-4 sm:flex-row sm:justify-end sm:space-x-2 sm:gap-0",
      className
    )}
    {...props}
  />
)
SheetFooter.displayName = "SheetFooter"

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title ref={ref} className={cn("text-lg font-semibold text-slate-900", className)} {...props} />
))
SheetTitle.displayName = DialogPrimitive.Title.displayName

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn("text-sm text-slate-600", className)} {...props} />
))
SheetDescription.displayName = DialogPrimitive.Description.displayName

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
